<template>
  <div class="membership-panel" :class="{ 'is-selected': isSelected }">
    <div class="panel-header">
      <!-- Picking between memberships only means something when there are
           several; on a single-membership account the radio is a control that
           cannot do anything. -->
      <b-form-radio
        v-if="selectable"
        class="panel-select"
        name="membership-selection"
        :checked="isSelected"
        :value="true"
        @change="$emit('select')"
      >
        <span class="panel-number">#{{ membershipNumber }}</span>
      </b-form-radio>
      <span v-else class="panel-number">#{{ membershipNumber }}</span>

      <b-badge v-if="context.isPrimary" variant="primary">Primary</b-badge>
      <b-badge :variant="statusVariant">{{ status }}</b-badge>
      <b-badge v-if="membershipBadge" :variant="membershipBadge.variant">{{
        membershipBadge.text
      }}</b-badge>
      <span v-if="cardType" class="panel-meta">{{ cardType }}</span>
      <span class="panel-meta">{{ expiration }}</span>
      <span class="panel-meta">{{ contactName }}</span>
      <ManageAutoRenewBanner
        v-if="isEnrolledInArb"
        compact
        :is-enrolled="true"
        :next-renewal-date="nextRenewalDate"
        :card-on-file="cardOnFile"
        :membership-sfid="context.membership.sfid"
        :existing-stripe-customer-id="stripeCustomerId"
        @cancelled="$emit('arb-cancelled')"
      />
      <span v-else class="panel-meta">Auto renew off</span>

      <div class="panel-actions">
        <span v-if="isSelected && selectable" class="renewing-flag"
          >Renewing below</span
        >
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="$emit('toggle')"
        >
          {{ context.expanded ? "Collapse" : "Expand" }}
        </b-button>
      </div>
    </div>

    <b-collapse :visible="context.expanded">
      <div class="panel-body">
        <p v-if="isSelected" class="panel-note">
          This membership is loaded in the form below. Edit it there, or make a
          quick correction here.
        </p>

        <b-row>
          <b-col cols="12" md="6">
            <h6 class="panel-section">
              Membership's Primary Contact
              <b-badge
                v-if="contactBadge"
                :variant="contactBadge.variant"
                class="ml-2"
                >{{ contactBadge.text }}</b-badge
              >
            </h6>
            <template v-if="contactFields">
              <ContactFieldset
                :contact="contactFields"
                :disabled="context.saving"
                :warning="warningFor(context.contact)"
                warning-variant="danger"
                @validity="setRecordValidity('membership-contact', $event)"
              />
            </template>
            <p v-else class="text-muted">
              No contact is linked to this membership.
            </p>

            <b-form-group
              label="Contact on this membership"
              label-size="sm"
              class="mt-3"
            >
              <b-form-select
                class="contact-picker"
                size="sm"
                :value="context.membership.membership_contact__c"
                :options="contactOptions"
                :disabled="context.saving"
                @change="$emit('set-contact', $event)"
              />
              <small class="text-muted"
                >Contacts belong to the account, so the same person may be used
                by more than one membership.</small
              >
            </b-form-group>

            <div class="panel-button-row">
              <b-button
                v-if="contactFields"
                size="sm"
                variant="primary"
                :disabled="
                  context.saving || !isRecordValid('membership-contact')
                "
                @click="$emit('update-contact', contactFields)"
                >Update Contact Information</b-button
              >
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="showAddContact = !showAddContact"
                >{{ showAddContact ? "Cancel" : "Add a contact" }}</b-button
              >
            </div>

            <div v-if="otherContacts.length" class="other-records">
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="showOtherContacts = !showOtherContacts"
              >
                {{ showOtherContacts ? "Hide" : "Show" }}
                {{ otherContacts.length }} other
                {{ otherContacts.length === 1 ? "contact" : "contacts" }}
              </b-button>

              <b-collapse :visible="showOtherContacts">
                <div
                  v-for="(other, index) in otherContacts"
                  :key="recordKey('contact', other, index)"
                  class="record-card"
                >
                  <div class="record-head">
                    <span class="record-title">{{ contactLabel(other) }}</span>
                    <b-badge v-if="other.contact_type__c" variant="secondary">{{
                      other.contact_type__c
                    }}</b-badge>
                    <b-badge
                      v-if="badgeFor(other)"
                      :variant="badgeFor(other).variant"
                      >{{ badgeFor(other).text }}</b-badge
                    >
                    <b-button
                      size="sm"
                      variant="link"
                      class="record-toggle"
                      @click="toggleRecord(recordKey('contact', other, index))"
                    >
                      {{
                        isRecordOpen(recordKey("contact", other, index))
                          ? "Close"
                          : "Edit"
                      }}
                    </b-button>
                  </div>

                  <b-collapse
                    :visible="isRecordOpen(recordKey('contact', other, index))"
                  >
                    <ContactFieldset
                      :contact="other"
                      :disabled="context.saving"
                      :warning="warningFor(other)"
                      warning-variant="danger"
                      @validity="
                        setRecordValidity(
                          recordKey('contact', other, index),
                          $event
                        )
                      "
                    />
                    <div class="panel-button-row">
                      <b-button
                        size="sm"
                        variant="primary"
                        :disabled="
                          context.saving ||
                          !isRecordValid(recordKey('contact', other, index))
                        "
                        @click="$emit('update-contact', other)"
                        >Update Contact Information</b-button
                      >
                      <b-button
                        size="sm"
                        variant="outline-primary"
                        :disabled="context.saving || !other.sfid"
                        @click="$emit('set-contact', other.sfid)"
                        >Use for this membership</b-button
                      >
                    </div>
                  </b-collapse>
                </div>
              </b-collapse>
            </div>

            <div v-if="showAddContact" class="add-form">
              <b-form-group label="First name" label-size="sm">
                <b-form-input v-model="newContact.firstname" size="sm" />
              </b-form-group>
              <b-form-group label="Last name" label-size="sm">
                <b-form-input v-model="newContact.lastname" size="sm" />
              </b-form-group>
              <b-form-group label="Email" label-size="sm">
                <b-form-input v-model="newContact.email" size="sm" />
                <span v-if="newContactErrors.email" class="text-danger small">{{
                  newContactErrors.email
                }}</span>
              </b-form-group>
              <b-form-group label="Country" label-size="sm">
                <b-form-select
                  v-model="newContact.phone_country"
                  :options="phoneCountries"
                  size="sm"
                />
              </b-form-group>
              <b-form-group label="Phone" label-size="sm">
                <b-input-group :prepend="newContactDialCode" size="sm">
                  <b-form-input v-model="newContact.phone" size="sm" />
                </b-input-group>
                <span v-if="newContactErrors.phone" class="text-danger small">{{
                  newContactErrors.phone
                }}</span>
              </b-form-group>
              <b-form-group label="Relationship" label-size="sm">
                <b-form-select
                  v-model="newContact.contact_type__c"
                  :options="contactTypeOptions"
                  size="sm"
                />
              </b-form-group>
              <b-button
                size="sm"
                variant="primary"
                :disabled="context.saving || newContactHasErrors"
                @click="submitNewContact"
                >Save Contact</b-button
              >
              <p class="panel-hint">
                Last name is required. To change which contact this membership
                uses, add them here and then pick them above.
              </p>
            </div>
          </b-col>

          <b-col cols="12" md="6">
            <h6 class="panel-section">
              Membership's Primary Boat
              <b-badge
                v-if="boatBadge"
                :variant="boatBadge.variant"
                class="ml-2"
                >{{ boatBadge.text }}</b-badge
              >
            </h6>
            <template v-if="boatFields">
              <BoatFieldset
                :boat="boatFields"
                :disabled="context.saving"
                :warning="warningFor(context.boat)"
                warning-variant="danger"
                is-primary-boat
                @validity="setRecordValidity('membership-boat', $event)"
              />
            </template>
            <p v-else class="text-muted">
              No boat is linked to this membership.
            </p>

            <div class="panel-button-row">
              <b-button
                v-if="boatFields"
                size="sm"
                variant="primary"
                :disabled="context.saving || !isRecordValid('membership-boat')"
                @click="$emit('update-boat', boatFields)"
                >Update Boat Information</b-button
              >
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="showAddBoat = !showAddBoat"
                >{{ showAddBoat ? "Cancel" : "Add a boat" }}</b-button
              >
            </div>

            <div v-if="otherBoats.length" class="other-records">
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="showOtherBoats = !showOtherBoats"
              >
                {{ showOtherBoats ? "Hide" : "Show" }}
                {{ otherBoats.length }} other
                {{ otherBoats.length === 1 ? "boat" : "boats" }}
              </b-button>

              <b-collapse :visible="showOtherBoats">
                <div
                  v-for="(other, index) in otherBoats"
                  :key="recordKey('boat', other, index)"
                  class="record-card"
                >
                  <div class="record-head">
                    <span class="record-title">{{ boatLabel(other) }}</span>
                    <b-badge variant="secondary">{{
                      other.boat_status__c || "No status"
                    }}</b-badge>
                    <b-badge
                      v-if="badgeFor(other)"
                      :variant="badgeFor(other).variant"
                      >{{ badgeFor(other).text }}</b-badge
                    >
                    <b-button
                      size="sm"
                      variant="link"
                      class="record-toggle"
                      @click="toggleRecord(recordKey('boat', other, index))"
                    >
                      {{
                        isRecordOpen(recordKey("boat", other, index))
                          ? "Close"
                          : "Edit"
                      }}
                    </b-button>
                  </div>

                  <b-collapse
                    :visible="isRecordOpen(recordKey('boat', other, index))"
                  >
                    <BoatFieldset
                      :boat="other"
                      :disabled="context.saving"
                      :warning="warningFor(other)"
                      warning-variant="danger"
                      @validity="
                        setRecordValidity(
                          recordKey('boat', other, index),
                          $event
                        )
                      "
                    />
                    <div class="panel-button-row">
                      <b-button
                        size="sm"
                        variant="primary"
                        :disabled="
                          context.saving ||
                          !isRecordValid(recordKey('boat', other, index))
                        "
                        @click="$emit('update-boat', other)"
                        >Update Boat Information</b-button
                      >
                    </div>
                    <p class="panel-hint">
                      Making this the primary boat can move the membership's
                      area of responsibility, and money moves with it, so that
                      change belongs in Salesforce.
                    </p>
                  </b-collapse>
                </div>
              </b-collapse>
            </div>

            <div v-if="showAddBoat" class="add-form">
              <b-form-group label="Boat name" label-size="sm">
                <b-form-input v-model="newBoat.name__c" size="sm" />
              </b-form-group>
              <b-form-group label="Make" label-size="sm">
                <b-form-input v-model="newBoat.boat_make__c" size="sm" />
              </b-form-group>
              <b-form-group label="Year" label-size="sm">
                <b-form-input v-model="newBoat.year__c" size="sm" />
                <span v-if="newBoatErrors.year" class="text-danger small">{{
                  newBoatErrors.year
                }}</span>
              </b-form-group>
              <b-form-group label="Length (ft)" label-size="sm">
                <b-form-input
                  v-model="newBoat.length__c"
                  type="number"
                  step="1"
                  min="1"
                  size="sm"
                />
                <span v-if="newBoatErrors.length" class="text-danger small">{{
                  newBoatErrors.length
                }}</span>
              </b-form-group>
              <b-form-group label="Registration / Document #" label-size="sm">
                <b-form-input
                  v-model="newBoat.registration_document__c"
                  size="sm"
                />
              </b-form-group>
              <p class="panel-hint">
                The new boat is added as a secondary vessel and inherits this
                membership's home port, so the area of responsibility does not
                move. To make it the primary boat, open the membership in
                Salesforce.
              </p>
              <b-button
                size="sm"
                variant="primary"
                :disabled="context.saving || newBoatHasErrors"
                @click="submitNewBoat"
                >Save Boat</b-button
              >
            </div>
          </b-col>
        </b-row>
      </div>
    </b-collapse>
  </div>
</template>

<script>
// One membership on the account. The membership currently loaded into the main
// form is marked selected; the rest collapse. Each panel edits its own records
// through its own update buttons, so a correction to a secondary membership
// does not require loading it into the form and losing the renewal in progress.
//
// Every contact and every boat is editable here, not just the two the
// membership points at. Listing a boat the dealer could see but not touch only
// told them to go and open Salesforce; the record was already on screen and the
// API already accepts a patch for it.
//
// Membership fields themselves are deliberately read-only here. Expiration and
// trailer care are what the renewal flow computes and prices, so editing them
// out of band would silently move a member's coverage.
import ManageAutoRenewBanner from "@/components/ManageAutoRenewBanner.vue";
import BoatFieldset from "./BoatFieldset.vue";
import ContactFieldset from "./ContactFieldset.vue";
import {
  boatYearError,
  boatLengthError,
  boatMakeError,
} from "./boatValidation";
import { contactTypesFor } from "@/config/contactTypes";
import { syncBadge, syncWarning } from "./syncStatus";
import { formatCalendarDate } from "@/utils/formatDate";
import { truthyBoolean } from "@/utils/truthyBoolean";
import {
  emailError,
  phoneError,
  normalizePhone,
  phoneCountryOptions,
  dialCodeFor,
  DEFAULT_PHONE_COUNTRY,
} from "./contactValidation";

export default {
  name: "MembershipPanel",
  components: { BoatFieldset, ContactFieldset, ManageAutoRenewBanner },
  props: {
    context: { type: Object, required: true },
    isSelected: { type: Boolean, default: false },
    // Whether choosing this membership over another is a meaningful action.
    selectable: { type: Boolean, default: true },
    // Contacts hang off the account, not the membership, so the picker offers
    // every contact on the account.
    accountContacts: { type: Array, default: () => [] },
    // The Salesforce record type new contacts are written under. It decides
    // which Contact_Type__c values are selectable.
    contactRecordTypeId: { type: String, required: true },
    // Stripe's customer is per ACCOUNT, so it is passed down rather than read
    // from the membership; cancelling a subscription needs both it and this
    // membership's own sfid.
    stripeCustomerId: { type: [String, Object], default: null },
    // For the membership loaded in the form below, these are the form's own
    // objects, so editing here and editing there are the same edit. Secondary
    // panels fall back to their context's records.
    boatModel: { type: Object, default: null },
    contactModel: { type: Object, default: null },
  },
  data() {
    return {
      showOtherBoats: false,
      showOtherContacts: false,
      showAddBoat: false,
      showAddContact: false,
      // Which secondary records the dealer has opened, and whether each one is
      // currently savable. Keyed by record so opening a second boat does not
      // close the first, and so one invalid record cannot disable another's
      // Save button.
      openRecords: {},
      recordValidity: {},
      newBoat: this.emptyBoat(),
      newContact: this.emptyContact(),
    };
  },
  computed: {
    boatFields() {
      return this.boatModel || this.context.boat;
    },
    contactFields() {
      return this.contactModel || this.context.contact;
    },
    newBoatErrors() {
      return {
        year: boatYearError(this.newBoat.year__c),
        length: boatLengthError(this.newBoat.length__c),
        make: boatMakeError(this.newBoat.boat_make__c),
      };
    },
    newBoatHasErrors() {
      return Object.values(this.newBoatErrors).some(Boolean);
    },
    membershipNumber() {
      return this.context.membership.membership_number__c || "—";
    },
    status() {
      return this.context.membership.membership_status__c || "No status";
    },
    statusVariant() {
      return this.status === "Active" ? "success" : "secondary";
    },
    membershipBadge() {
      return syncBadge(this.context.membership);
    },
    // The record the membership points at, not the object being edited: the
    // form's model carries the field values but not Heroku Connect's columns.
    contactBadge() {
      return syncBadge(this.context.contact);
    },
    boatBadge() {
      return syncBadge(this.context.boat);
    },
    cardType() {
      return this.context.membership.membership_type__c || "";
    },
    expiration() {
      const date = this.context.membership.membership_expiration_date__c;
      return date ? `Exp ${date}` : "No expiration set";
    },
    contactName() {
      const contact = this.context.contact;
      if (!contact) return "No contact";
      return [contact.firstname, contact.lastname].filter(Boolean).join(" ");
    },
    otherBoats() {
      const primarySfid = this.context.boat && this.context.boat.sfid;
      return this.context.boats.filter((boat) => boat.sfid !== primarySfid);
    },
    // Every other contact on the account. They are not "the membership's"
    // contacts - contacts belong to the account - but they are the people a
    // dealer looking at this membership is likely to need to correct.
    otherContacts() {
      const currentSfid =
        this.context.membership.membership_contact__c ||
        (this.context.contact && this.context.contact.sfid);
      return this.accountContacts.filter(
        (contact) => contact.sfid !== currentSfid
      );
    },
    contactOptions() {
      const options = this.accountContacts.map((contact) => ({
        value: contact.sfid,
        text: this.contactLabel(contact),
      }));
      if (!options.length) {
        return [{ value: null, text: "No contacts on this account" }];
      }
      return options;
    },
    phoneCountries() {
      return phoneCountryOptions();
    },
    newContactDialCode() {
      return dialCodeFor(this.newContact.phone_country);
    },
    newContactErrors() {
      return {
        // A new contact must be reachable, so both are required here even
        // though an existing record may predate the rule.
        email: emailError(this.newContact.email, { required: true }),
        phone: phoneError(this.newContact.phone, { required: true }),
        lastname: this.newContact.lastname ? null : "Last name is required",
      };
    },
    newContactHasErrors() {
      return Object.values(this.newContactErrors).some(Boolean);
    },
    contactTypeOptions() {
      // The record type decides which values Salesforce will accept; the panel
      // then drops Primary Contact, because choosing which contact a membership
      // uses is what the picker above does, by writing membership_contact__c.
      return contactTypesFor(this.contactRecordTypeId)
        .filter((value) => value !== "Primary Contact")
        .map((value) => ({ value, text: value }));
    },
    // Auto-renew belongs to THIS membership. The account-level banner this
    // replaced could only describe whichever membership was loaded into the
    // form, so on an account holding several it was silently wrong about the
    // rest - while offering a Cancel button.
    isEnrolledInArb() {
      return truthyBoolean(this.context.membership.auto_renew__c);
    },
    cardOnFile() {
      const membership = this.context.membership;
      const brand = membership.active_arb_card_type;
      const last4 = membership.active_arb_last_4_digits;
      if (!brand && !last4) return null;
      return [brand, last4 ? `····${last4}` : null].filter(Boolean).join(" ");
    },
    nextRenewalDate() {
      // When the charge goes out: the membership's own expiration date.
      return formatCalendarDate(
        this.context.membership.membership_expiration_date__c
      );
    },
    autoRenewSummary() {
      const membership = this.context.membership;
      if (!truthyBoolean(membership.auto_renew__c)) return "Auto renew off";
      const last4 = membership.active_arb_last_4_digits;
      return last4 ? `Auto renew ····${last4}` : "Auto renew on";
    },
  },
  methods: {
    emptyBoat() {
      return {
        name__c: null,
        boat_make__c: null,
        year__c: null,
        length__c: null,
        registration_document__c: null,
      };
    },
    emptyContact() {
      return {
        firstname: null,
        lastname: null,
        email: null,
        phone: null,
        phone_country: DEFAULT_PHONE_COUNTRY,
        contact_type__c: "Other",
      };
    },
    // Stable per-record key. A record that has never synced has no sfid, so
    // fall back to the external id and finally to position.
    recordKey(kind, record, index) {
      const id =
        record.sfid || record.heroku_external_id__c || `index-${index}`;
      return `${kind}:${id}`;
    },
    isRecordOpen(key) {
      return !!this.openRecords[key];
    },
    toggleRecord(key) {
      this.$set(this.openRecords, key, !this.openRecords[key]);
    },
    setRecordValidity(key, valid) {
      this.$set(this.recordValidity, key, valid);
    },
    // Unknown means the fieldset has not reported yet, which only happens
    // before it mounts. Treat that as savable rather than disabling a button
    // the dealer is looking at.
    isRecordValid(key) {
      return this.recordValidity[key] !== false;
    },
    warningFor(record) {
      return syncWarning(record);
    },
    badgeFor(record) {
      return syncBadge(record);
    },
    boatLabel(boat) {
      const label = [boat.year__c, boat.boat_make__c, boat.name__c]
        .filter(Boolean)
        .join(" ");
      return label || boat.sfid;
    },
    contactLabel(contact) {
      return (
        [contact.firstname, contact.lastname].filter(Boolean).join(" ") ||
        contact.email ||
        contact.sfid
      );
    },
    submitNewBoat() {
      this.$emit("add-boat", { ...this.newBoat });
      this.newBoat = this.emptyBoat();
      this.showAddBoat = false;
    },
    submitNewContact() {
      const contact = { ...this.newContact };
      // phone_country drives the prefix shown in the form; Salesforce has no
      // column for it, so it never leaves the component.
      delete contact.phone_country;
      this.$emit("add-contact", {
        ...contact,
        // Salesforce has one phone column and no dial-code field, so store the
        // ten digits the rest of the app writes. phone_country is a UI concern.
        phone: normalizePhone(contact.phone),
      });
      this.newContact = this.emptyContact();
      this.showAddContact = false;
    },
  },
};
</script>

<style scoped>
.membership-panel {
  border: 1px solid var(--border-input);
  border-radius: 10px;
  margin-bottom: 0.75rem;
  background: var(--bg-surface);
  /* Long contact names must not push the actions past the card edge. */
  overflow: hidden;
}

.is-selected {
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.75rem 1rem;
}

.panel-select {
  margin-bottom: 0;
}

.panel-number {
  font-weight: 600;
  color: var(--text-primary);
}

.panel-meta {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.panel-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.renewing-flag {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--accent);
}

.panel-body {
  padding: 0 1rem 1rem;
  border-top: 1px solid var(--border-default);
}

.panel-section {
  margin-top: 1rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.panel-note {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0.75rem 0 0;
}

.panel-button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.add-form {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border: 1px dashed var(--border-input);
  border-radius: 8px;
}

.other-records {
  margin-top: 0.75rem;
}

.record-card {
  margin-top: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-default);
  border-radius: 8px;
}

.record-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-size: 0.85rem;
}

.record-title {
  color: var(--text-primary);
  font-weight: 600;
}

.record-toggle {
  margin-left: auto;
  padding: 0;
}

.panel-hint {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin-top: 0.5rem;
}
</style>
